export interface PrivateService {
  id: string;
  title: string;
  shortDescription: string;
  fullDescription: string;
  whoIsFor: string[];
  faqs: { question: string; answer: string }[];
  bookingUrl: string;
  seoTitle: string;
  metaDescription: string;
  category: 'infection' | 'injection' | 'ear' | 'health' | 'bloodtest' | 'clinical';
  dedicatedPage?: string;
}

export interface ClinicalService {
  id: string;
  title: string;
  shortDescription: string;
  fullDescription: string;
  whoIsFor: string[];
  faqs: { question: string; answer: string }[];
  bookingUrl: string;
  seoTitle: string;
  metaDescription: string;
  category: 'clinical';
  image?: string;
  price?: number;
  whyChoose?: string[];
  whatWeDo?: string[];
  dedicatedPage?: string;
}

export const clinicalServices: ClinicalService[] = [
  {
    id: 'travel-clinic',
    title: 'Travel Clinic & Vaccinations',
    shortDescription: 'Comprehensive travel vaccinations including Yellow Fever certification and destination-specific advice.',
    fullDescription: 'Our Travel Clinic offers a wide range of travel vaccinations, anti-malarials and destination-specific health advice. As an official Yellow Fever centre, we provide certified vaccinations required for many countries. Our pharmacists will assess your travel plans and recommend the appropriate vaccinations and preventive measures.',
    whoIsFor: [
      'Travellers to tropical destinations',
      'Business travellers',
      'Gap year students',
      'Those needing Yellow Fever certification'
    ],
    faqs: [
      { question: 'Do I need an appointment?', answer: 'Yes — please book a travel consultation so we can assess your needs.' },
      { question: 'How far in advance should I book?', answer: 'Ideally 6-8 weeks before travel to allow time for vaccine courses.' },
      { question: 'Do you provide Yellow Fever certificates?', answer: 'Yes — we are an official Yellow Fever vaccination centre.' }
    ],
    bookingUrl: 'https://healthclinicshull.setmore.com/',
    seoTitle: 'Travel Clinic & Vaccinations in Hull | Wilberforce Pharmacy',
    metaDescription: 'Travel vaccinations and health advice in Hull. Official Yellow Fever centre. Book your travel consultation at Wilberforce Pharmacy.',
    category: 'clinical',
    image: 'travel-clinic'
  },
  {
    id: 'phlebotomy-service',
    title: 'Phlebotomy Service',
    shortDescription: 'Safe, professional blood sample collection carried out by trained healthcare professionals in a clean, confidential setting.',
    fullDescription: 'The Phlebotomy Service at Wilberforce Pharmacy provides safe, professional blood sample collection carried out by trained healthcare professionals in a clean, confidential setting. Phlebotomy is the process of taking blood samples from a vein for laboratory testing. These samples may be used to assess a wide range of health markers, including hormones, vitamins, iron levels, infection markers and chronic conditions. Having blood taken by a trained phlebotomist helps ensure correct technique, minimal discomfort, reduced risk of sample rejection and accurate test results. Our service is ideal if you already have a blood test request from a GP, private doctor or consultant, specialist clinic, or workplace/occupational health provider. We focus purely on accurate blood sample collection, ensuring your samples are taken correctly and handled according to clinical standards.',
    whoIsFor: [
      'Those with a blood test request form from a GP or consultant',
      'Private laboratory testing requirements',
      'Monitoring existing medical conditions',
      'Specialist or consultant-led care patients',
      'Employment or insurance blood sample requirements'
    ],
    faqs: [
      { question: 'Do I need a referral?', answer: 'Yes. You will usually need a blood test request from a GP, consultant, or private provider unless the blood test is booked directly through our private testing services.' },
      { question: 'Do you analyse the blood tests?', answer: 'No. We collect the blood sample only. Samples are sent to the laboratory specified on your request form for analysis.' },
      { question: 'Do I need to fast before my blood test?', answer: 'This depends on the test requested. Please follow the instructions given by your clinician or laboratory. If unsure, ask before attending.' },
      { question: 'Is the service suitable for nervous patients?', answer: 'Yes. Our team is experienced in supporting patients who feel anxious about blood tests and will take time to ensure your comfort.' },
      { question: 'How long does the appointment take?', answer: 'Most phlebotomy appointments take 10–15 minutes.' },
      { question: 'Is the service private?', answer: 'Yes. All blood samples are taken in a private consultation room.' },
      { question: 'Can children have blood taken?', answer: 'This depends on age, test type and clinical suitability. Please contact us in advance to confirm.' },
      { question: 'What should I bring to my appointment?', answer: 'Please bring your blood test request form (paper or digital), any laboratory instructions provided, and photo ID if requested by your provider.' }
    ],
    bookingUrl: 'https://healthclinicshull.setmore.com/services/0c3a66d8-aa1e-4c5e-b8fd-d1e88034c5e7',
    seoTitle: 'Phlebotomy Service in Hull | Blood Sample Collection | Wilberforce Pharmacy',
    metaDescription: 'Professional phlebotomy and blood sample collection in Hull. Trained healthcare professionals, clean clinical environment, confidential service. £40 at Wilberforce Pharmacy.',
    category: 'clinical',
    image: 'phlebotomy',
    price: 40,
    whyChoose: [
      'Trusted local pharmacy in Hull',
      'Trained healthcare professionals',
      'Clean, clinical environment',
      'Confidential and efficient service',
      'Convenient appointment times'
    ],
    whatWeDo: [
      'Collect blood samples requested by your clinician',
      'Follow correct labelling and handling procedures',
      'Provide a calm, professional environment',
      'Ensure confidentiality and infection control standards'
    ]
  },
  {
    id: 'weight-management',
    title: 'Weight Loss Treatment',
    shortDescription: 'Professional weight loss treatment and counseling to help you achieve a healthier lifestyle.',
    fullDescription: 'Our comprehensive weight management service offers professional support, treatment options and ongoing monitoring to help you achieve sustainable weight loss. We provide clinically supervised programmes including prescription medications where appropriate, combined with lifestyle advice.',
    whoIsFor: [
      'Adults with raised BMI',
      'Those struggling with weight loss',
      'People seeking professional support',
      'Individuals wanting sustainable results'
    ],
    faqs: [
      { question: 'What treatments are available?', answer: 'We offer various options including prescription medications and lifestyle support.' },
      { question: 'Do I need regular check-ups?', answer: 'Yes — monitoring is essential for safe and effective treatment.' },
      { question: 'Is this service confidential?', answer: 'Yes — all consultations are completely private.' }
    ],
    bookingUrl: 'https://healthclinicshull.setmore.com/',
    seoTitle: 'Weight Management in Hull | Weight Loss Support | Wilberforce Pharmacy',
    metaDescription: 'Professional weight management and weight loss support in Hull. Clinically supervised programmes at Wilberforce Pharmacy.',
    category: 'clinical',
    image: 'weight-loss',
    dedicatedPage: '/weight-loss-hull'
  }
];

export const privateServices: PrivateService[] = [
  {
    id: 'oral-thrush',
    title: 'Oral Thrush Treatment',
    shortDescription: 'Fast assessment and treatment for oral fungal infections.',
    fullDescription: 'Oral thrush is a common fungal infection caused by an overgrowth of Candida yeast. It can cause white patches in the mouth, soreness, redness and difficulty eating or drinking. Wilberforce Pharmacy offers confidential oral thrush assessment and treatment without the need to see your GP.',
    whoIsFor: [
      'Adults with white patches in the mouth',
      'People using inhalers',
      'Denture wearers',
      'People with sore or burning mouth symptoms'
    ],
    faqs: [
      { question: 'What are the symptoms of oral thrush?', answer: 'White patches on the tongue or cheeks, soreness, redness and altered taste.' },
      { question: 'Do I need a GP appointment?', answer: 'No — our pharmacist can assess and treat eligible patients.' },
      { question: 'Is treatment safe?', answer: 'Yes — treatments are clinically approved and provided following assessment.' }
    ],
    bookingUrl: 'https://healthclinicshull.setmore.com/',
    seoTitle: 'Oral Thrush Treatment in Hull | Wilberforce Pharmacy',
    metaDescription: 'Fast oral thrush treatment in Hull. Private pharmacist consultation at Wilberforce Pharmacy. No GP needed.',
    category: 'infection'
  },
  {
    id: 'ear-infection',
    title: 'Ear Infection Assessment',
    shortDescription: 'Professional review and treatment for mild ear infections.',
    fullDescription: 'Ear infections can cause pain, swelling, discharge or hearing changes. Wilberforce Pharmacy provides pharmacist assessment and treatment where appropriate.',
    whoIsFor: [
      'Mild ear pain',
      'Outer ear infections',
      'Redness or itching'
    ],
    faqs: [
      { question: 'Can pharmacists treat ear infections?', answer: 'Yes — for eligible cases following assessment.' },
      { question: 'When should I see a GP?', answer: 'Severe pain, fever or discharge may need GP referral.' },
      { question: 'Is treatment immediate?', answer: 'Where appropriate, yes.' }
    ],
    bookingUrl: 'https://healthclinicshull.setmore.com/',
    seoTitle: 'Ear Infection Treatment in Hull | Wilberforce Pharmacy',
    metaDescription: 'Ear infection assessment in Hull without GP wait. Professional pharmacist consultation available.',
    category: 'infection'
  },
  {
    id: 'vitamin-b12',
    title: 'Vitamin B12 Injection',
    shortDescription: 'Support for low energy, fatigue and deficiency.',
    fullDescription: 'Vitamin B12 is essential for energy production, nerve function and red blood cell formation. Low levels may cause fatigue, brain fog, weakness and low mood. Wilberforce Pharmacy offers private Vitamin B12 injections following pharmacist assessment.',
    whoIsFor: [
      'People with diagnosed B12 deficiency',
      'Individuals with fatigue or low energy',
      'Vegetarians and vegans',
      'People with absorption issues'
    ],
    faqs: [
      { question: 'Do I need a blood test first?', answer: 'In some cases yes — your pharmacist will advise.' },
      { question: 'How long does it last?', answer: 'Effects may last several weeks depending on individual needs.' },
      { question: 'Is the injection painful?', answer: 'Most people experience minimal discomfort.' }
    ],
    bookingUrl: 'https://healthclinicshull.setmore.com/services/5320c5de-96d0-4e23-bb8a-4b2b6cfc6b82',
    seoTitle: 'Vitamin B12 Injection in Hull | Wilberforce Pharmacy',
    metaDescription: 'Vitamin B12 injections in Hull for low energy, fatigue and deficiency. Book safely at Wilberforce Pharmacy.',
    category: 'injection'
  },
  {
    id: 'weight-loss',
    title: 'Mounjaro Weight Loss',
    shortDescription: 'Clinically supervised weight management support.',
    fullDescription: 'Weight loss injections can help support appetite control and weight management when combined with healthy lifestyle changes. Wilberforce Pharmacy provides a safe, medically supervised weight loss service following eligibility assessment.',
    whoIsFor: [
      'Adults with raised BMI',
      'Individuals struggling with appetite control',
      'Those seeking professional support'
    ],
    faqs: [
      { question: 'Do I need an assessment?', answer: 'Yes — safety screening is essential.' },
      { question: 'Is ongoing monitoring required?', answer: 'Yes — follow-ups are part of safe treatment.' },
      { question: 'Are results guaranteed?', answer: 'Results vary and depend on lifestyle changes.' }
    ],
    bookingUrl: 'https://healthclinicshull.setmore.com/services/61cdb4d3-c3e5-4f77-a681-a25d64f9158b',
    seoTitle: 'Weight Loss Injections in Hull | Wilberforce Pharmacy',
    metaDescription: 'Clinically supervised weight loss injections in Hull. Private consultation and ongoing support at Wilberforce Pharmacy.',
    category: 'injection',
    dedicatedPage: '/mounjaro-weight-loss'
  },
  {
    id: 'ear-syringing',
    title: 'Ear Syringing / Irrigation',
    shortDescription: 'Safe removal of earwax using controlled water pressure.',
    fullDescription: 'Excess earwax can cause hearing loss, discomfort and blocked ears. Ear irrigation is a safe method to remove wax using controlled water pressure. Wilberforce Pharmacy offers professional earwax removal services.',
    whoIsFor: [
      'Blocked ears',
      'Reduced hearing',
      'Ear fullness or discomfort'
    ],
    faqs: [
      { question: 'Is ear syringing safe?', answer: 'Yes, when performed by trained professionals.' },
      { question: 'Does it hurt?', answer: 'Most patients experience minimal discomfort.' },
      { question: 'How long does it take?', answer: 'Usually 15–30 minutes.' }
    ],
    bookingUrl: 'https://healthclinicshull.setmore.com/services/ab80f6c0-88a1-4d4a-9334-4dce2979a513',
    seoTitle: 'Ear Syringing in Hull | Wilberforce Pharmacy',
    metaDescription: 'Safe ear syringing and irrigation in Hull. Remove earwax professionally at Wilberforce Pharmacy.',
    category: 'ear'
  },
  {
    id: 'microsuction',
    title: 'Microsuction Earwax Removal',
    shortDescription: 'Gold-standard earwax removal method.',
    fullDescription: 'Microsuction is the gold-standard method of earwax removal. It uses gentle suction rather than water and is suitable for many patients.',
    whoIsFor: [
      'Sensitive ears',
      'Hearing aid users',
      'Perforated eardrum history',
      'Frequent earwax build-up'
    ],
    faqs: [
      { question: 'Is microsuction better than syringing?', answer: 'Often yes — it is gentler and more precise.' },
      { question: 'Is it noisy?', answer: 'You may hear suction sounds briefly.' },
      { question: 'Can both ears be treated?', answer: 'Yes, if required.' }
    ],
    bookingUrl: 'https://healthclinicshull.setmore.com/services/c02d60d6-d41e-4264-bda7-fd6fe2399408',
    seoTitle: 'Microsuction Earwax Removal in Hull | Wilberforce Pharmacy',
    metaDescription: 'Gold-standard microsuction earwax removal in Hull. Professional service at Wilberforce Pharmacy.',
    category: 'ear'
  },
  {
    id: 'period-delay',
    title: 'Period Delay Service',
    shortDescription: 'Short-term delay of menstruation for travel or events.',
    fullDescription: 'Period delay treatment can help postpone your menstrual cycle for important events, holidays or religious occasions. Our pharmacist provides a confidential consultation to assess suitability and supply treatment where appropriate.',
    whoIsFor: [
      'Women with regular cycles',
      'Travel or holiday planning',
      'Weddings or religious events'
    ],
    faqs: [
      { question: 'How many days can my period be delayed?', answer: 'Usually up to 17 days depending on treatment.' },
      { question: 'Is it safe?', answer: 'For suitable patients, yes — assessment is required.' },
      { question: 'Will it affect fertility?', answer: 'No long-term effects when used correctly.' }
    ],
    bookingUrl: 'https://healthclinicshull.setmore.com/',
    seoTitle: 'Period Delay Tablets in Hull | Wilberforce Pharmacy',
    metaDescription: 'Need to delay your period? Book a private period delay consultation in Hull with Wilberforce Pharmacy.',
    category: 'health'
  },
  {
    id: 'erectile-dysfunction',
    title: 'Erectile Dysfunction Treatment',
    shortDescription: 'Confidential assessment and treatment for ED.',
    fullDescription: 'Erectile dysfunction is a common condition affecting men of all ages and can be caused by stress, health conditions or circulation problems. Wilberforce Pharmacy offers discreet assessment and treatment following pharmacist consultation.',
    whoIsFor: [
      'Men experiencing difficulty maintaining an erection',
      'Occasional or ongoing ED',
      'Those seeking private treatment'
    ],
    faqs: [
      { question: 'Is the consultation confidential?', answer: 'Yes — completely private.' },
      { question: 'Do I need blood tests?', answer: 'Not usually; assessment is based on medical history.' },
      { question: 'Is treatment safe?', answer: 'Yes, when supplied following assessment.' }
    ],
    bookingUrl: 'https://healthclinicshull.setmore.com/services/1ffa4204-1e47-4536-bc4d-0b3d44a20888',
    seoTitle: 'Erectile Dysfunction Treatment in Hull | Wilberforce Pharmacy',
    metaDescription: 'Confidential erectile dysfunction treatment in Hull. Private consultation at Wilberforce Pharmacy.',
    category: 'health'
  },
  {
    id: 'haemorrhoids',
    title: 'Haemorrhoids (Piles) Treatment',
    shortDescription: 'Relief from itching, discomfort and swelling.',
    fullDescription: 'Haemorrhoids (piles) are swollen blood vessels in or around the anus that can cause discomfort, itching, pain or bleeding. Wilberforce Pharmacy provides assessment and treatment without a GP appointment.',
    whoIsFor: [
      'Mild to moderate piles',
      'Itching, discomfort or pain',
      'Occasional bleeding (non-severe)'
    ],
    faqs: [
      { question: 'What symptoms can be treated?', answer: 'Itching, swelling, discomfort and mild bleeding.' },
      { question: 'When should I see a GP?', answer: 'If bleeding is heavy, persistent or painful.' },
      { question: 'Are treatments effective?', answer: 'Most people improve within a few days.' }
    ],
    bookingUrl: 'https://healthclinicshull.setmore.com/',
    seoTitle: 'Haemorrhoids Treatment in Hull | Wilberforce Pharmacy',
    metaDescription: 'Fast haemorrhoids treatment in Hull. Private pharmacist consultation at Wilberforce Pharmacy.',
    category: 'health'
  },
  // Blood Tests
  {
    id: 'hba1c-blood-test',
    title: 'HbA1c Blood Test',
    shortDescription: 'Measures average blood sugar levels over 2–3 months for diabetes screening.',
    fullDescription: 'The HbA1c blood test measures your average blood sugar levels over the past 2–3 months. It is commonly used to diagnose diabetes and monitor blood sugar control. This test provides a reliable picture of how well blood sugar has been managed over time, helping identify diabetes and pre-diabetes without requiring fasting.',
    whoIsFor: [
      'People with diabetes',
      'Those with symptoms of high blood sugar',
      'Individuals with family history of diabetes',
      'Anyone wanting routine health screening'
    ],
    faqs: [
      { question: 'Do I need to fast?', answer: 'No — fasting is not required for this test.' },
      { question: 'How long does the result take?', answer: 'Results are usually available within a few days.' },
      { question: 'Can this diagnose diabetes?', answer: 'Yes — it helps identify diabetes and pre-diabetes.' }
    ],
    bookingUrl: 'https://healthclinicshull.setmore.com/services/1b6f150b-fc50-4beb-930f-c49f6bd85728',
    seoTitle: 'HbA1c Blood Test in Hull | Diabetes Check | Wilberforce Pharmacy',
    metaDescription: 'Private HbA1c blood test in Hull to check average blood sugar levels and screen for diabetes at Wilberforce Pharmacy.',
    category: 'bloodtest'
  },
  {
    id: 'psa-blood-test',
    title: 'PSA Blood Test',
    shortDescription: 'Prostate-specific antigen test to assess prostate health in men.',
    fullDescription: 'The PSA test measures prostate-specific antigen levels in the blood and is used to assess prostate health in men. It can help detect potential prostate issues early, although elevated levels can occur for several reasons and do not automatically indicate cancer.',
    whoIsFor: [
      'Men aged 45+',
      'Those with urinary symptoms',
      'Men with family history of prostate disease'
    ],
    faqs: [
      { question: 'Does a high PSA mean cancer?', answer: 'Not always — levels can rise for several reasons including infection or enlargement.' },
      { question: 'Should I avoid anything before the test?', answer: 'Avoid ejaculation and vigorous exercise 48 hours beforehand.' },
      { question: 'Is this a screening test?', answer: 'It helps assess risk but does not confirm diagnosis alone.' }
    ],
    bookingUrl: 'https://healthclinicshull.setmore.com/services/6359bd3d-f6bc-4849-b75d-45b0e2ae32d0',
    seoTitle: 'PSA Blood Test in Hull | Prostate Health Check | Wilberforce Pharmacy',
    metaDescription: 'Private PSA blood test in Hull to check prostate health. Confidential testing available at Wilberforce Pharmacy.',
    category: 'bloodtest'
  },
  {
    id: 'bhcg-blood-test',
    title: 'β-HCG Blood Test',
    shortDescription: 'Pregnancy hormone test for early confirmation and monitoring.',
    fullDescription: 'The β-HCG test measures the pregnancy hormone (human chorionic gonadotropin) in the blood and can detect pregnancy earlier than urine tests. It provides a more sensitive and accurate measurement of pregnancy hormone levels, useful for early confirmation and monitoring.',
    whoIsFor: [
      'Early pregnancy confirmation',
      'Monitoring pregnancy hormone levels',
      'Investigation of pregnancy-related concerns'
    ],
    faqs: [
      { question: 'Is it more accurate than a urine test?', answer: 'Yes — it detects lower hormone levels than home pregnancy tests.' },
      { question: 'Can it show how far along I am?', answer: 'Levels help estimate early pregnancy progression.' },
      { question: 'Do I need to fast?', answer: 'No fasting is required.' }
    ],
    bookingUrl: 'https://healthclinicshull.setmore.com/',
    seoTitle: 'β-HCG Blood Test in Hull | Pregnancy Hormone Test | Wilberforce Pharmacy',
    metaDescription: 'Private β-HCG blood test in Hull to confirm or monitor pregnancy. Fast and confidential service at Wilberforce Pharmacy.',
    category: 'bloodtest'
  },
  {
    id: 'testosterone-blood-test',
    title: 'Testosterone Blood Test',
    shortDescription: 'Measures testosterone levels affecting energy, mood and sexual health.',
    fullDescription: 'This test measures testosterone levels which affect energy, mood, muscle mass and sexual health. Low testosterone can cause symptoms such as fatigue, low libido, reduced muscle mass and mood changes. Both men and women can benefit from testosterone assessment.',
    whoIsFor: [
      'Low libido',
      'Fatigue and low energy',
      'Erectile difficulties',
      'Reduced muscle mass'
    ],
    faqs: [
      { question: 'When is the best time to test?', answer: 'Morning testing is recommended as testosterone levels are highest.' },
      { question: 'Can women have this test?', answer: 'Yes — for hormone assessment and imbalance investigation.' }
    ],
    bookingUrl: 'https://healthclinicshull.setmore.com/',
    seoTitle: 'Testosterone Blood Test in Hull | Hormone Check | Wilberforce Pharmacy',
    metaDescription: 'Private testosterone blood test in Hull for libido, energy and hormone balance assessment at Wilberforce Pharmacy.',
    category: 'bloodtest'
  },
  {
    id: 'ferritin-blood-test',
    title: 'Ferritin Blood Test',
    shortDescription: 'Measures iron stores to detect deficiency or overload.',
    fullDescription: 'Ferritin measures iron stores in the body and helps detect iron deficiency or overload. Low ferritin is a common cause of fatigue, hair loss and weakness, especially in women with heavy periods. This test helps identify whether iron supplementation may be beneficial.',
    whoIsFor: [
      'Fatigue or weakness',
      'Hair loss',
      'Heavy periods',
      'Iron deficiency concerns'
    ],
    faqs: [
      { question: 'Does low ferritin mean anaemia?', answer: 'It may indicate iron deficiency which can lead to anaemia if untreated.' },
      { question: 'Is fasting needed?', answer: 'No fasting is required for this test.' }
    ],
    bookingUrl: 'https://healthclinicshull.setmore.com/services/f80328dc-0e68-4718-bc43-11560e8b216b',
    seoTitle: 'Ferritin Blood Test in Hull | Iron Stores Check | Wilberforce Pharmacy',
    metaDescription: 'Private ferritin blood test in Hull to check iron stores and investigate fatigue at Wilberforce Pharmacy.',
    category: 'bloodtest'
  },
  {
    id: 'vitamin-b12-blood-test',
    title: 'Vitamin B12 Blood Test',
    shortDescription: 'Measures B12 levels essential for nerve health and energy.',
    fullDescription: 'This test measures Vitamin B12 levels which are essential for nerve health, energy production and red blood cell formation. Deficiency can cause tiredness, weakness, nerve problems and cognitive issues. Testing helps identify whether B12 supplementation or injections may be beneficial.',
    whoIsFor: [
      'Tiredness and fatigue',
      'Nerve symptoms like tingling',
      'Vegetarians and vegans',
      'Those with absorption issues'
    ],
    faqs: [
      { question: 'Can I have injections after this test?', answer: 'Yes — if clinically appropriate based on your results.' },
      { question: 'Do supplements affect results?', answer: 'Yes — inform your pharmacist of any supplements you take.' }
    ],
    bookingUrl: 'https://healthclinicshull.setmore.com/',
    seoTitle: 'Vitamin B12 Blood Test in Hull | Wilberforce Pharmacy',
    metaDescription: 'Private Vitamin B12 blood test in Hull to check deficiency and fatigue causes at Wilberforce Pharmacy.',
    category: 'bloodtest'
  },
  {
    id: 'ccp-blood-test',
    title: 'CCP Blood Test',
    shortDescription: 'Detects antibodies commonly present in rheumatoid arthritis.',
    fullDescription: 'The CCP (Cyclic Citrullinated Peptide) test detects antibodies commonly present in rheumatoid arthritis. This test supports clinical assessment and can help identify rheumatoid arthritis earlier in the disease process, allowing for earlier treatment intervention.',
    whoIsFor: [
      'Joint pain or stiffness',
      'Suspected rheumatoid arthritis',
      'Family history of autoimmune conditions'
    ],
    faqs: [
      { question: 'Is it diagnostic on its own?', answer: 'No — it supports clinical assessment alongside symptoms and examination.' }
    ],
    bookingUrl: 'https://healthclinicshull.setmore.com/',
    seoTitle: 'CCP Blood Test in Hull | Rheumatoid Arthritis Check | Wilberforce Pharmacy',
    metaDescription: 'Private CCP antibody blood test in Hull to support rheumatoid arthritis investigation at Wilberforce Pharmacy.',
    category: 'bloodtest'
  },
  {
    id: 'ige-blood-test',
    title: 'IgE Blood Test',
    shortDescription: 'Measures immunoglobulin E associated with allergic reactions.',
    fullDescription: 'The IgE blood test measures immunoglobulin E levels which are associated with allergic reactions. Elevated IgE levels may indicate allergies, asthma or other allergic conditions. This test complements allergy assessment and can help identify underlying allergic causes.',
    whoIsFor: [
      'Suspected allergies',
      'Chronic itching or rashes',
      'Hay fever symptoms',
      'Asthma investigation'
    ],
    faqs: [
      { question: 'Does it replace skin testing?', answer: 'It complements allergy assessment and can be used alongside skin testing.' }
    ],
    bookingUrl: 'https://healthclinicshull.setmore.com/',
    seoTitle: 'IgE Blood Test in Hull | Allergy Testing | Wilberforce Pharmacy',
    metaDescription: 'Private IgE blood test in Hull to investigate allergies at Wilberforce Pharmacy.',
    category: 'bloodtest'
  },
  {
    id: 'vitamin-d-blood-test',
    title: 'Vitamin D Blood Test',
    shortDescription: 'Measures vitamin D levels for bone, immune and muscle health.',
    fullDescription: 'The 25-OH Vitamin D test measures vitamin D levels essential for bone, immune and muscle health. Vitamin D deficiency is common in the UK, especially during winter months, and can cause fatigue, bone pain and weakness. Testing helps determine whether supplementation is needed.',
    whoIsFor: [
      'Fatigue or tiredness',
      'Bone or muscle pain',
      'Limited sun exposure',
      'Winter health checks'
    ],
    faqs: [
      { question: 'Do I need to fast?', answer: 'No fasting is required.' },
      { question: 'Is deficiency common in the UK?', answer: 'Yes — especially during winter months when sun exposure is limited.' }
    ],
    bookingUrl: 'https://healthclinicshull.setmore.com/',
    seoTitle: 'Vitamin D Blood Test in Hull | 25-OH Vitamin D Test | Wilberforce Pharmacy',
    metaDescription: 'Private Vitamin D blood test in Hull to assess deficiency and bone health at Wilberforce Pharmacy.',
    category: 'bloodtest'
  },
  {
    id: 'fsh-blood-test',
    title: 'FSH Blood Test',
    shortDescription: 'Fertility and menopause hormone assessment.',
    fullDescription: 'The FSH (Follicle Stimulating Hormone) blood test measures hormone levels important for fertility and reproductive health. It is commonly used to assess ovarian function, investigate menopause and evaluate fertility in both men and women.',
    whoIsFor: [
      'Fertility concerns',
      'Menopause investigation',
      'Irregular periods',
      'Reproductive health assessment'
    ],
    faqs: [
      { question: 'When should I have this test?', answer: 'Your pharmacist will advise on optimal timing based on your cycle.' },
      { question: 'Can men have this test?', answer: 'Yes — FSH is important for male fertility assessment too.' }
    ],
    bookingUrl: 'https://healthclinicshull.setmore.com/',
    seoTitle: 'FSH Blood Test in Hull | Fertility & Menopause Test | Wilberforce Pharmacy',
    metaDescription: 'Private FSH blood test in Hull for fertility and menopause assessment at Wilberforce Pharmacy.',
    category: 'bloodtest'
  },
  {
    id: 'lh-blood-test',
    title: 'LH Blood Test',
    shortDescription: 'Ovulation and reproductive hormone function test.',
    fullDescription: 'The LH (Luteinizing Hormone) blood test measures hormone levels important for ovulation and reproductive function. LH surges trigger ovulation in women and is important for testosterone production in men. This test helps assess fertility and hormonal balance.',
    whoIsFor: [
      'Ovulation tracking',
      'Fertility investigation',
      'Polycystic ovary syndrome (PCOS)',
      'Hormonal imbalance assessment'
    ],
    faqs: [
      { question: 'When is the best time to test?', answer: 'Timing depends on what is being investigated — your pharmacist will advise.' },
      { question: 'Is this test useful for men?', answer: 'Yes — LH affects testosterone production.' }
    ],
    bookingUrl: 'https://healthclinicshull.setmore.com/',
    seoTitle: 'LH Blood Test in Hull | Ovulation & Fertility Test | Wilberforce Pharmacy',
    metaDescription: 'Private LH blood test in Hull for ovulation tracking and fertility assessment at Wilberforce Pharmacy.',
    category: 'bloodtest'
  },
  {
    id: 'prolactin-blood-test',
    title: 'Prolactin Blood Test',
    shortDescription: 'Tests for fertility and hormone imbalance.',
    fullDescription: 'The Prolactin (PRL) blood test measures prolactin hormone levels which affect fertility and reproductive health. Elevated prolactin can cause menstrual irregularities, fertility issues and in some cases, milk production. This test helps investigate hormonal imbalances.',
    whoIsFor: [
      'Irregular periods',
      'Fertility concerns',
      'Unexplained milk production',
      'Hormone imbalance investigation'
    ],
    faqs: [
      { question: 'What causes high prolactin?', answer: 'Various factors including stress, medications or pituitary conditions.' },
      { question: 'Do I need to fast?', answer: 'No, but avoid stress before the test as it can affect levels.' }
    ],
    bookingUrl: 'https://healthclinicshull.setmore.com/',
    seoTitle: 'Prolactin Blood Test in Hull | Hormone Imbalance Test | Wilberforce Pharmacy',
    metaDescription: 'Private prolactin blood test in Hull for fertility and hormone imbalance investigation at Wilberforce Pharmacy.',
    category: 'bloodtest'
  },
  {
    id: 'estradiol-blood-test',
    title: 'Estradiol (E2) Blood Test',
    shortDescription: 'Measures estrogen levels for cycle and hormone health.',
    fullDescription: 'The Estradiol (E2) blood test measures the main form of estrogen in the body. It is important for menstrual cycle regulation, fertility, bone health and menopausal assessment. This test helps evaluate ovarian function and hormone balance.',
    whoIsFor: [
      'Menstrual irregularities',
      'Fertility assessment',
      'Menopause symptoms',
      'Hormone replacement therapy monitoring'
    ],
    faqs: [
      { question: 'When should this test be done?', answer: 'Timing depends on your cycle and what is being investigated.' },
      { question: 'Can men have this test?', answer: 'Yes — men also produce estradiol and imbalances can occur.' }
    ],
    bookingUrl: 'https://healthclinicshull.setmore.com/',
    seoTitle: 'Estradiol Blood Test in Hull | Estrogen Levels Test | Wilberforce Pharmacy',
    metaDescription: 'Private estradiol blood test in Hull for estrogen levels and cycle health assessment at Wilberforce Pharmacy.',
    category: 'bloodtest'
  }
];

export const serviceCategories = [
  { id: 'infection', label: 'Infection & Treatment', icon: '🦠', heroImage: 'infection-hero' },
  { id: 'injection', label: 'Injections & Health Support', icon: '💉', heroImage: 'injection-hero' },
  { id: 'ear', label: 'Ear Care Services', icon: '👂', heroImage: 'ear-hero' },
  { id: 'health', label: "Men's & Women's Health", icon: '❤️', heroImage: 'health-hero' },
  { id: 'bloodtest', label: 'Private Blood Tests', icon: '🩸', heroImage: 'bloodtest-hero' },
  { id: 'bloodprofile', label: 'Blood Test Profiles', icon: '📊', heroImage: 'bloodtest-hero' }
];

export interface BloodTestProfile {
  id: string;
  title: string;
  shortDescription: string;
  testsIncluded: string[];
  suitableFor: string[];
  price: string;
  faqs: { question: string; answer: string }[];
  bookingUrl: string;
  seoTitle: string;
  metaDescription: string;
}

export const bloodTestProfiles: BloodTestProfile[] = [
  {
    id: 'diabetes-health-profile',
    title: 'Diabetes Health Profile',
    shortDescription: 'The Diabetes Health Profile uses the HbA1c blood test to assess your average blood sugar levels over the last 2–3 months.',
    testsIncluded: ['HbA1c'],
    suitableFor: [
      'People with diabetes',
      'Family history of diabetes',
      'Symptoms such as fatigue, thirst or frequent urination',
      'Routine health screening'
    ],
    price: '£35',
    faqs: [
      { question: 'Do I need to fast?', answer: 'No — fasting is not required for HbA1c testing.' },
      { question: 'Can this diagnose diabetes?', answer: 'It helps identify diabetes and pre-diabetes but diagnosis should be confirmed by a clinician.' },
      { question: 'How often should I test?', answer: 'Usually every 3–6 months if monitoring diabetes.' }
    ],
    bookingUrl: 'https://healthclinicshull.setmore.com/',
    seoTitle: 'Diabetes Blood Test Profile in Hull | HbA1c Test',
    metaDescription: 'Private diabetes blood test in Hull. HbA1c testing to check average blood sugar levels. Fast, confidential service at Wilberforce Pharmacy.'
  },
  {
    id: 'mens-health-profile',
    title: "Men's Health Profile",
    shortDescription: "The Men's Health Profile supports assessment of prostate health and male hormone balance.",
    testsIncluded: ['PSA (Prostate Specific Antigen)', 'Testosterone'],
    suitableFor: [
      'Men aged 45+',
      'Low energy or fatigue',
      'Reduced libido',
      'Urinary symptoms',
      'Routine male health screening'
    ],
    price: '£95',
    faqs: [
      { question: 'Does a high PSA mean cancer?', answer: 'No — PSA can rise for several reasons including infection or enlargement.' },
      { question: 'When is testosterone best tested?', answer: 'Morning testing is recommended for accuracy.' },
      { question: 'Should I avoid anything before testing?', answer: 'Avoid ejaculation and strenuous exercise for 48 hours before PSA testing.' }
    ],
    bookingUrl: 'https://healthclinicshull.setmore.com/',
    seoTitle: "Men's Health Blood Test in Hull | PSA & Testosterone",
    metaDescription: "Private men's health blood test in Hull including PSA and testosterone. Confidential service at Wilberforce Pharmacy."
  },
  {
    id: 'pregnancy-profile',
    title: 'Pregnancy & Early Hormone Profile',
    shortDescription: 'This profile uses a β-HCG blood test to confirm or monitor early pregnancy.',
    testsIncluded: ['β-HCG'],
    suitableFor: [
      'Early pregnancy confirmation',
      'When urine tests are unclear',
      'Monitoring hormone progression'
    ],
    price: '£40',
    faqs: [
      { question: 'Is this more accurate than a urine test?', answer: 'Yes — blood testing detects pregnancy earlier.' },
      { question: 'Can it tell how far along I am?', answer: 'Hormone levels help estimate progression but scans confirm dating.' },
      { question: 'Do I need to fast?', answer: 'No.' }
    ],
    bookingUrl: 'https://healthclinicshull.setmore.com/',
    seoTitle: 'Pregnancy Blood Test in Hull | β-HCG Test',
    metaDescription: 'Private pregnancy blood test in Hull. Early pregnancy confirmation with β-HCG testing at Wilberforce Pharmacy.'
  },
  {
    id: 'female-hormone-profile',
    title: 'Female Hormone & Fertility Profile',
    shortDescription: 'This profile assesses key hormones involved in fertility, menstrual cycles and menopause.',
    testsIncluded: ['FSH', 'LH', 'Prolactin (PRL)', 'Estradiol (E2)'],
    suitableFor: [
      'Irregular or absent periods',
      'Fertility investigations',
      'Perimenopause symptoms',
      'Hormonal imbalance'
    ],
    price: '£130',
    faqs: [
      { question: 'Does cycle timing matter?', answer: 'Yes — some hormones must be tested on specific cycle days.' },
      { question: 'Can this confirm menopause?', answer: 'It supports assessment but clinical review is needed.' },
      { question: 'Can I test while on contraception?', answer: 'Results may be affected — discuss during booking.' }
    ],
    bookingUrl: 'https://healthclinicshull.setmore.com/',
    seoTitle: 'Female Hormone Blood Test in Hull | Fertility Profile',
    metaDescription: 'Private female hormone blood test in Hull. Fertility and menopause assessment at Wilberforce Pharmacy.'
  },
  {
    id: 'fatigue-iron-profile',
    title: 'Energy, Fatigue & Iron Profile',
    shortDescription: 'This is one of the most popular profiles and checks common causes of tiredness and low energy.',
    testsIncluded: ['Ferritin', 'Vitamin B12', 'Vitamin D (25-OH)'],
    suitableFor: [
      'Persistent fatigue',
      'Hair thinning',
      'Heavy periods',
      'Brain fog',
      'Low mood or weakness'
    ],
    price: '£95',
    faqs: [
      { question: 'Do supplements affect results?', answer: 'Yes — inform your pharmacist before testing.' },
      { question: 'Is this suitable for vegans?', answer: 'Yes — especially helpful for B12 assessment.' },
      { question: 'Is fatigue usually vitamin-related?', answer: 'Often, but other causes may exist — results help guide next steps.' }
    ],
    bookingUrl: 'https://healthclinicshull.setmore.com/',
    seoTitle: 'Fatigue Blood Test Profile in Hull | Energy & Iron',
    metaDescription: 'Private fatigue blood test in Hull. Check ferritin, B12 and vitamin D for energy and tiredness at Wilberforce Pharmacy.'
  },
  {
    id: 'vitamin-nutrition-profile',
    title: 'Vitamin & Nutrition Profile',
    shortDescription: 'This profile checks two essential vitamins linked to energy, immunity and bone health.',
    testsIncluded: ['Vitamin B12', 'Vitamin D (25-OH)'],
    suitableFor: [
      'Low energy',
      'Frequent illness',
      'Limited sun exposure',
      'Vegetarians or vegans'
    ],
    price: '£70',
    faqs: [
      { question: 'Is vitamin D deficiency common?', answer: 'Yes — especially in the UK winter.' },
      { question: 'Can I take supplements after testing?', answer: 'Yes — guidance can be given after results.' }
    ],
    bookingUrl: 'https://healthclinicshull.setmore.com/',
    seoTitle: 'Vitamin Blood Test Profile in Hull | B12 & Vitamin D',
    metaDescription: 'Private vitamin blood test in Hull. Check B12 and vitamin D levels at Wilberforce Pharmacy.'
  },
  {
    id: 'allergy-profile',
    title: 'Allergy Profile',
    shortDescription: 'This profile measures IgE, a marker commonly raised in allergic conditions.',
    testsIncluded: ['IgE'],
    suitableFor: [
      'Hay fever',
      'Skin rashes',
      'Itching',
      'Suspected allergies'
    ],
    price: '£65',
    faqs: [
      { question: 'Does this identify specific allergens?', answer: 'It shows allergy activity but not exact triggers.' },
      { question: 'Does this replace allergy clinics?', answer: 'No — it supports initial investigation.' }
    ],
    bookingUrl: 'https://healthclinicshull.setmore.com/',
    seoTitle: 'Allergy Blood Test Profile in Hull | IgE Testing',
    metaDescription: 'Private allergy blood test in Hull. IgE testing for allergies and hay fever at Wilberforce Pharmacy.'
  },
  {
    id: 'autoimmune-profile',
    title: 'Inflammatory & Autoimmune Profile',
    shortDescription: 'This profile checks CCP antibodies, often associated with rheumatoid arthritis.',
    testsIncluded: ['CCP'],
    suitableFor: [
      'Joint pain or stiffness',
      'Swollen joints',
      'Family history of autoimmune disease'
    ],
    price: '£55',
    faqs: [
      { question: 'Does this diagnose arthritis?', answer: 'No — it supports assessment alongside symptoms.' },
      { question: 'Is it useful early?', answer: 'Yes — CCP can be positive before symptoms worsen.' }
    ],
    bookingUrl: 'https://healthclinicshull.setmore.com/',
    seoTitle: 'Autoimmune Blood Test Profile in Hull | CCP Test',
    metaDescription: 'Private autoimmune blood test in Hull. CCP testing for rheumatoid arthritis at Wilberforce Pharmacy.'
  },
  {
    id: 'core-wellness-profile',
    title: 'Core Wellness Blood Test Profile',
    shortDescription: 'A broad health overview covering blood sugar, iron and vitamin status.',
    testsIncluded: ['HbA1c', 'Ferritin', 'Vitamin B12', 'Vitamin D (25-OH)'],
    suitableFor: [
      'Annual health checks',
      'Preventative screening',
      'General wellbeing monitoring'
    ],
    price: '£120',
    faqs: [
      { question: 'Is this a full MOT?', answer: 'It provides an excellent baseline overview.' },
      { question: 'Do I need fasting?', answer: 'No.' }
    ],
    bookingUrl: 'https://healthclinicshull.setmore.com/',
    seoTitle: 'Core Wellness Blood Test Profile in Hull | Health Check',
    metaDescription: 'Private core wellness blood test in Hull. Comprehensive health check at Wilberforce Pharmacy.'
  }
];
